/*
 * THE SKY: which of the world's tiles are worth having, and how much detail to draw them in.
 *
 * Ported from `reference/universe-v8/engine.js` — `skyView` (867–871), `pumpSky` (872–892) and
 * `skyLOD` (1001–1004). Checked against those by running them; see `sky.test.ts`.
 *
 * THIS IS WHY THEIRS IS FAST AND OURS WAS NOT. Our renderer holds one array of every figure and
 * redraws all of it every frame. Theirs never holds the field at all: the world is cut into tiles
 * of 2,400 units, and only the tiles the viewport touches are fetched — nearest first, a couple at
 * a time while the camera is moving so the network does not fight the frame. A tile that has not
 * arrived is not missing, it is a cloud, because the index knows its name-count before its contents.
 *
 * Everything here is a pure function of the camera, the viewport and an index. It holds no state
 * and touches no canvas, so it can be checked against the reference exactly.
 */

/** One tile's entry in the index: where it is, and how many names it holds. */
export interface SkyTile {
  id: string;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** The name count, known before the tile loads — what lets an unloaded tile draw as a cloud. */
  n: number;
}

/** The world rectangle the camera can see, in world units. */
export interface SkyView {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

/** A camera: where it looks and how close. */
export interface SkyCamera {
  x: number;
  y: number;
  z: number;
}

/** The viewport, in CSS pixels. */
export interface SkyViewport {
  width: number;
  height: number;
}

/**
 * WHAT THE CAMERA CAN SEE, in world units.
 *
 * `squash` is the vertical foreshortening the tilted zoomed-out view applies (`this.SQ`); it is 1
 * when the map is flat on. `pad` widens the rectangle so tiles are asked for BEFORE they are
 * needed — the reference pumps at 1.35, which is a third of a screen of warning at the edges.
 */
export function skyView(cam: SkyCamera, view: SkyViewport, squash: number, pad: number): SkyView {
  const sq = squash || 1;
  const m = pad || 1;
  const hw = (view.width / 2 / cam.z) * m;
  const hh = ((view.height / 2 / cam.z) * m) / sq;
  return { x0: cam.x - hw, x1: cam.x + hw, y0: cam.y / sq - hh, y1: cam.y / sq + hh };
}

/** How far the tiles are fetched ahead of the edge of the screen. The reference's 1.35. */
export const SKY_PAD = 1.35;

/**
 * WHICH TILES TO ASK FOR, NEAREST FIRST.
 *
 * Every tile the padded viewport touches that is not already spoken for, ordered by how far its
 * centre is from the camera — so what the reader is looking at arrives before what they are only
 * heading toward. Distances are compared in the SQUASHED space the view is measured in, and left
 * squared, because only the order matters.
 *
 * `idle` decides what is not yet spoken for; the caller owns that state, because "asked for",
 * "arriving", "ingesting" and "failed" are all its business and none of it is arithmetic.
 */
export function tilesWanted(
  index: readonly SkyTile[],
  cam: SkyCamera,
  view: SkyViewport,
  squash: number,
  idle: (id: string) => boolean,
): string[] {
  const v = skyView(cam, view, squash, SKY_PAD);
  const sq = squash || 1;
  const want: { id: string; d: number }[] = [];
  for (const tile of index) {
    if (!idle(tile.id)) continue;
    if (tile.x1 < v.x0 || tile.x0 > v.x1 || tile.y1 < v.y0 || tile.y0 > v.y1) continue;
    const mx = (tile.x0 + tile.x1) / 2 - cam.x;
    const my = (tile.y0 + tile.y1) / 2 - cam.y / sq;
    want.push({ id: tile.id, d: mx * mx + my * my });
  }
  want.sort((a, b) => a.d - b.d);
  return want.map(w => w.id);
}

/**
 * HOW MANY TILES MAY BE IN FLIGHT AT ONCE.
 *
 * Fewer while the camera is moving. A fetch that lands mid-flight has to be parsed and ingested,
 * and the reference would rather arrive late than drop a frame during the one moment the reader is
 * watching the field move.
 */
export function skyParallel(moving: boolean): number {
  return moving ? 2 : 4;
}

/** How often the tile list is recomputed, in milliseconds. Not every frame: the answer barely moves. */
export const SKY_PUMP_MS = 150;

/**
 * WHERE THE FOUR BANDS BEGIN — three absolute zooms, and therefore three arguments.
 *
 * Theirs are 0.5, 0.22 and 0.03 against a rim of 12,320. THEY WERE BAKED IN HERE UNTIL 2026-08-19,
 * and the ledger called the unit done with the numbers written in its own notes column, which is
 * how a rule-2 breach hides: it looks like documentation. A zoom is pixels per world unit, so
 * copied into a world an eighth the size these fire at the wrong distances entirely — the band that
 * draws the whole field as dots would never be reached, and the far view would try to draw discs.
 */
export interface SkyBands {
  /** At or above this, everything: discs, labels, the web. */
  everything: number;
  /** At or above this, discs without labels. */
  discs: number;
  /** At or above this, one dot a name. Below it, clumps. */
  dots: number;
}

/** Theirs, at their rim — the fixture that makes a comparison meaningful, and nothing else. */
export const THEIR_SKY_BANDS: SkyBands = { everything: 0.5, discs: 0.22, dots: 0.03 };

/**
 * HOW MUCH DETAIL THE FIELD IS DRAWN IN, from the zoom alone.
 *
 * Four bands, and the whole field is drawn differently in each — this is the gate our renderer
 * never had. `useLOD: false` turns the scheme off entirely, which is the reference's own escape
 * hatch for looking at what it costs without it.
 *
 * 0 is everything; 3 is the furthest out, where names are clumps rather than figures.
 */
export function skyLOD(z: number, bands: SkyBands = THEIR_SKY_BANDS, useLOD = true): number {
  if (useLOD === false) return 0;
  return z >= bands.everything ? 0 : z >= bands.discs ? 1 : z >= bands.dots ? 2 : 3;
}

/*
 * THE WORLD'S OWN DIMENSIONS ARE DATA, NOT MECHANISM.
 *
 * The reference's rim is 12,320 and its tiles are 2,400 units, because that is where its 10,000
 * mock names happened to settle — 300 real ones and 9,700 synthetic filler. Ours will be neither
 * number. Baking theirs in was the first tunnel-vision mistake of this port and it was caught in
 * the second unit: the engine must be told how big the world is, never assume it.
 */
export interface SkyWorld {
  /** Where the naming stops — the outer bound of the field. */
  rim: number;
  /** World units per tile. Their build uses 2,400; ours comes from whatever the pipeline emits. */
  tile: number;
}

/*
 * These three ARE mechanism — but they are mechanism measured in world units, so they are
 * calibrated against a rim and do not survive a world of a different size unchanged. Held as
 * fractions of the rim rather than as their absolute numbers, so a bigger world gets bigger cells
 * rather than sixteen times as many of them.
 *
 * Their values at rim 12,320: 400, 1,200 and 4,200.
 */
/** World units per sky cell — the unit of "what is on screen". */
export function gridCell(world: SkyWorld): number {
  return world.rim / 30.8;
}
/** World units per clump cell, for the furthest level of detail. */
export function clumpCell(world: SkyWorld): number {
  return world.rim / 10.267;
}
/** A tie longer than this is a rumour jump, never part of the ambient web. */
export function longTie(world: SkyWorld): number {
  return world.rim / 2.933;
}
